import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Link,
  InputAdornment,
  IconButton,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { updatePassword } from '../api/services';

// Shared look (design tokens) - same as the rest of IZHub
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
  },
};

const labelSx = { fontWeight: 600, color: ui.text };

// Password validation constants
const PASSWORD_MIN_LENGTH = 6;

// Validates passwords, including old password
const validatePassword = (oldPassword, newPassword, confirmPassword) => {
  if (!oldPassword) {
    return 'Current password is required';
  }
  if (!newPassword || !confirmPassword) {
    return 'Both new password fields are required';
  }
  if (newPassword !== confirmPassword) {
    return 'New passwords do not match';
  }
  if (newPassword.length < PASSWORD_MIN_LENGTH) {
    return `New password must be at least ${PASSWORD_MIN_LENGTH} characters long`;
  }
  return null;
};

const AccountSettings = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleOldPasswordChange = (e) => {
    setOldPassword(e.target.value.trim());
    setError(null);
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value.trim());
    setError(null);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value.trim());
    setError(null);
  };

  // Handles password update
  const handleUpdatePassword = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    const validationError = validatePassword(oldPassword, newPassword, confirmPassword);
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      await updatePassword(oldPassword, newPassword, confirmPassword);
      setSuccess('Password updated successfully');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        (err.code === 'ERR_NETWORK' ? 'Network error, please try again later' : 'Failed to update password');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [oldPassword, newPassword, confirmPassword]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading && !validatePassword(oldPassword, newPassword, confirmPassword)) {
      handleUpdatePassword();
    }
  };

  const isFormValid = !validatePassword(oldPassword, newPassword, confirmPassword) && !loading;

  const visibilityAdornment = {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={handleClickShowPassword}
          edge="end"
          sx={{ color: ui.muted }}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };

  return (
    <Box
      sx={{
        borderRadius: '16px',
        border: `1px solid ${ui.border}`,
        backgroundColor: '#fff',
        boxShadow: ui.shadow,
        p: { xs: 2.5, sm: 3.5 },
      }}
      role="region"
      aria-label="Account Settings"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Section header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: ui.primarySoft,
              color: ui.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockOutlinedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: ui.text, lineHeight: 1.3 }}>
              <Link
                href="/settings"
                sx={{
                  color: ui.text,
                  '&:hover': { color: ui.primary },
                  textDecoration: 'none',
                }}
              >
                Account Settings
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ color: ui.muted }}>
              Change your password
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && (
            <Alert
              severity="error"
              variant="filled"
              onClose={() => setError(null)}
              role="alert"
              aria-live="assertive"
              sx={{ borderRadius: '10px' }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              variant="filled"
              onClose={() => setSuccess(null)}
              role="alert"
              aria-live="polite"
              sx={{ borderRadius: '10px' }}
            >
              {success}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, gridColumn: { sm: '1 / -1' } }}>
              <Typography
                variant="body2"
                sx={labelSx}
                component="label"
                htmlFor="current-password-input"
              >
                Current Password
              </Typography>
              <TextField
                id="current-password-input"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={oldPassword}
                onChange={handleOldPasswordChange}
                onKeyDown={handleKeyDown}
                variant="outlined"
                size="small"
                aria-label="Current password"
                helperText="Enter your current password"
                sx={{ ...fieldSx, maxWidth: { sm: 'calc(50% - 12px)' } }}
                InputProps={visibilityAdornment}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="body2"
                sx={labelSx}
                component="label"
                htmlFor="password-input"
              >
                New Password
              </Typography>
              <TextField
                id="password-input"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={handleNewPasswordChange}
                onKeyDown={handleKeyDown}
                variant="outlined"
                size="small"
                aria-label="New password"
                helperText="Use 6+ characters with uppercase, lowercase, number, and special character"
                sx={fieldSx}
                InputProps={visibilityAdornment}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="body2"
                sx={labelSx}
                component="label"
                htmlFor="confirm-password-input"
              >
                Confirm Password
              </Typography>
              <TextField
                id="confirm-password-input"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onKeyDown={handleKeyDown}
                variant="outlined"
                size="small"
                aria-label="Confirm new password"
                helperText="Must match the new password"
                sx={fieldSx}
                InputProps={visibilityAdornment}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              pt: 2.5,
              borderTop: `1px solid ${ui.border}`,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: ui.primary,
                color: 'white',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 3,
                py: 1.1,
                boxShadow: 'none',
                minWidth: 160,
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  bgcolor: ui.primaryDark,
                  boxShadow: 'none',
                },
                '&.Mui-disabled': { backgroundColor: '#d5dbe0', color: '#8a949e' },
              }}
              onClick={handleUpdatePassword}
              disabled={!isFormValid}
              aria-label="Update password"
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AccountSettings;